import { useContext, useEffect } from 'react';

import { ConfigContext } from 'Context/config';
import { UserContext } from 'Context/user';

import { isOfficeHours } from 'Utilities/isOfficeHours/isOfficeHours';

import { getAgentTeamsAvailability } from 'Services/privateApi';

type AgentTeamsAvailabilityResponse = {
  data?: {
    profilingTeamAvailable?: boolean;
  };
};

const POLL_INTERVAL_MS = 180_000;

export const useAgentAvailability = () => {
  const { configActions } = useContext(ConfigContext);
  const { userState } = useContext(UserContext);

  const isLoggedIn = Number.isInteger(userState?.id);

  useEffect(() => {
    if (!isLoggedIn) {
      return undefined;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    configActions.update({ agentAvailability: { liveChatAvailable: isOfficeHours() } });

    const fetchAvailability = () => {
      getAgentTeamsAvailability()
        .then((response: AgentTeamsAvailabilityResponse) => {
          if (cancelled) {
            return;
          }
          const available = response?.data?.profilingTeamAvailable ?? isOfficeHours();
          configActions.update({ agentAvailability: { liveChatAvailable: available } });
        })
        .catch((error: Error) => {
          if (cancelled) {
            return;
          }
          configActions.update({ agentAvailability: { liveChatAvailable: isOfficeHours() } });
          window?.Sentry?.captureException?.(error, {
            level: 'warning',
            tags: { scope: 'useAgentAvailability' },
          });
        });
    };

    const startPolling = () => {
      if (intervalId !== undefined) {
        return;
      }
      intervalId = setInterval(fetchAvailability, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalId === undefined) {
        return;
      }
      clearInterval(intervalId);
      intervalId = undefined;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchAvailability();
        startPolling();
      }
    };

    if (!document.hidden) {
      fetchAvailability();
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps
};
